/**
 * Интерфейс для объекта ошибки валидации
 */
export interface ValidationError {
  code: number;
  message: string;
}

/**
 * Валидация БИК (Банковский идентификационный код)
 * @param bik - БИК для проверки
 * @param error - Объект ошибки
 * @returns Результат валидации
 */
export function validateBik(bik: string | number, error: ValidationError): boolean {
  let result = false;
  if (typeof bik === 'number') {
    bik = bik.toString();
  } else if (typeof bik !== 'string') {
    bik = '';
  }
  if (!bik.length) {
    error.code = 1;
    error.message = 'БИК пуст';
  } else if (/[^0-9]/.test(bik)) {
    error.code = 2;
    error.message = 'БИК может состоять только из цифр';
  } else if (bik.length !== 9) {
    error.code = 3;
    error.message = 'БИК может состоять только из 9 цифр';
  } else {
    result = true;
  }
  return result;
}

/**
 * Валидация ИНН (Идентификационный номер налогоплательщика)
 * @param inn - ИНН для проверки
 * @param error - Объект ошибки
 * @returns Результат валидации
 */
export function validateInn(inn: string | number, error: ValidationError): boolean {
  let result = false;
  if (typeof inn === 'number') {
    inn = inn.toString();
  } else if (typeof inn !== 'string') {
    inn = '';
  }
  if (!inn.length) {
    error.code = 1;
    error.message = 'ИНН пуст';
  } else if (/[^0-9]/.test(inn)) {
    error.code = 2;
    error.message = 'ИНН может состоять только из цифр';
  } else if ([10, 12].indexOf(inn.length) === -1) {
    error.code = 3;
    error.message = 'ИНН может состоять только из 10 или 12 цифр';
  } else {
    const checkDigit = function (inn: string, coefficients: number[]): number {
      let n = 0;
      for (let i = 0; i < coefficients.length; i++) {
        n += coefficients[i] * parseInt(inn[i]);
      }
      return parseInt(((n % 11) % 10).toString());
    };
    switch (inn.length) {
      case 10:
        const n10 = checkDigit(inn, [2, 4, 10, 3, 5, 9, 4, 6, 8]);
        if (n10 === parseInt(inn[9])) {
          result = true;
        }
        break;
      case 12:
        const n11 = checkDigit(inn, [7, 2, 4, 10, 3, 5, 9, 4, 6, 8]);
        const n12 = checkDigit(inn, [3, 7, 2, 4, 10, 3, 5, 9, 4, 6, 8]);
        if (n11 === parseInt(inn[10]) && n12 === parseInt(inn[11])) {
          result = true;
        }
        break;
    }
    if (!result) {
      error.code = 4;
      error.message = 'Неправильное контрольное число';
    }
  }
  return result;
}

/**
 * Валидация КПП (Код причины постановки на учет)
 * @param kpp - КПП для проверки
 * @param error - Объект ошибки
 * @returns Результат валидации
 */
export function validateKpp(kpp: string | number, error: ValidationError): boolean {
  let result = false;
  if (typeof kpp === 'number') {
    kpp = kpp.toString();
  } else if (typeof kpp !== 'string') {
    kpp = '';
  }
  if (!kpp.length) {
    error.code = 1;
    error.message = 'КПП пуст';
  } else if (kpp.length !== 9) {
    error.code = 2;
    error.message = 'КПП может состоять только из 9 знаков (цифр или заглавных букв латинского алфавита от A до Z)';
  } else if (!/^[0-9]{4}[0-9A-Z]{2}[0-9]{3}$/.test(kpp)) {
    error.code = 3;
    error.message = 'Неправильный формат КПП';
  } else {
    result = true;
  }
  return result;
}

/**
 * Валидация КС (Корреспондентский счет)
 * @param ks - КС для проверки
 * @param bik - БИК банка
 * @param error - Объект ошибки
 * @returns Результат валидации
 */
export function validateKs(ks: string | number, bik: string | number, error: ValidationError): boolean {
  let result = false;
  if (validateBik(bik, error)) {
    if (typeof ks === 'number') {
      ks = ks.toString();
    } else if (typeof ks !== 'string') {
      ks = '';
    }
    if (!ks.length) {
      error.code = 1;
      error.message = 'К/С пуст';
    } else if (/[^0-9]/.test(ks)) {
      error.code = 2;
      error.message = 'К/С может состоять только из цифр';
    } else if (ks.length !== 20) {
      error.code = 3;
      error.message = 'К/С может состоять только из 20 цифр';
    } else {
      const bikKs = '0' + bik.toString().slice(4, 6) + ks;
      let checksum = 0;
      const coefficients = [7, 1, 3, 7, 1, 3, 7, 1, 3, 7, 1, 3, 7, 1, 3, 7, 1, 3, 7, 1, 3, 7, 1];
      for (let i = 0; i < coefficients.length; i++) {
        checksum += coefficients[i] * (parseInt(bikKs[i]) % 10);
      }
      if (checksum % 10 === 0) {
        result = true;
      } else {
        error.code = 4;
        error.message = 'Неправильное контрольное число';
      }
    }
  }
  return result;
}

/**
 * Валидация ОГРН (Основной государственный регистрационный номер)
 * @param ogrn - ОГРН для проверки
 * @param error - Объект ошибки
 * @returns Результат валидации
 */
export function validateOgrn(ogrn: string | number, error: ValidationError): boolean {
  let result = false;
  if (typeof ogrn === 'number') {
    ogrn = ogrn.toString();
  } else if (typeof ogrn !== 'string') {
    ogrn = '';
  }
  if (!ogrn.length) {
    error.code = 1;
    error.message = 'ОГРН пуст';
  } else if (/[^0-9]/.test(ogrn)) {
    error.code = 2;
    error.message = 'ОГРН может состоять только из цифр';
  } else if (ogrn.length !== 13) {
    error.code = 3;
    error.message = 'ОГРН может состоять только из 13 цифр';
  } else {
    const n13 = parseInt((parseInt(ogrn.slice(0, -1)) % 11).toString().slice(-1));
    if (n13 === parseInt(ogrn[12])) {
      result = true;
    } else {
      error.code = 4;
      error.message = 'Неправильное контрольное число';
    }
  }
  return result;
}

/**
 * Валидация ОГРНИП (Основной государственный регистрационный номер индивидуального предпринимателя)
 * @param ogrnip - ОГРНИП для проверки
 * @param error - Объект ошибки
 * @returns Результат валидации
 */
export function validateOgrnip(ogrnip: string | number, error: ValidationError): boolean {
  let result = false;
  if (typeof ogrnip === 'number') {
    ogrnip = ogrnip.toString();
  } else if (typeof ogrnip !== 'string') {
    ogrnip = '';
  }
  if (!ogrnip.length) {
    error.code = 1;
    error.message = 'ОГРНИП пуст';
  } else if (/[^0-9]/.test(ogrnip)) {
    error.code = 2;
    error.message = 'ОГРНИП может состоять только из цифр';
  } else if (ogrnip.length !== 15) {
    error.code = 3;
    error.message = 'ОГРНИП может состоять только из 15 цифр';
  } else {
    const n15 = parseInt((parseInt(ogrnip.slice(0, -1)) % 13).toString().slice(-1));
    if (n15 === parseInt(ogrnip[14])) {
      result = true;
    } else {
      error.code = 4;
      error.message = 'Неправильное контрольное число';
    }
  }
  return result;
}

/**
 * Валидация РС (Расчетный счет)
 * @param rs - РС для проверки
 * @param bik - БИК банка
 * @param error - Объект ошибки
 * @returns Результат валидации
 */
export function validateRs(rs: string | number, bik: string | number, error: ValidationError): boolean {
  let result = false;
  if (validateBik(bik, error)) {
    if (typeof rs === 'number') {
      rs = rs.toString();
    } else if (typeof rs !== 'string') {
      rs = '';
    }
    if (!rs.length) {
      error.code = 1;
      error.message = 'Р/С пуст';
    } else if (/[^0-9]/.test(rs)) {
      error.code = 2;
      error.message = 'Р/С может состоять только из цифр';
    } else if (rs.length !== 20) {
      error.code = 3;
      error.message = 'Р/С может состоять только из 20 цифр';
    } else {
      const bikRs = bik.toString().slice(-3) + rs;
      let checksum = 0;
      const coefficients = [7, 1, 3, 7, 1, 3, 7, 1, 3, 7, 1, 3, 7, 1, 3, 7, 1, 3, 7, 1, 3, 7, 1];
      for (let i = 0; i < coefficients.length; i++) {
        checksum += coefficients[i] * (parseInt(bikRs[i]) % 10);
      }
      if (checksum % 10 === 0) {
        result = true;
      } else {
        error.code = 4;
        error.message = 'Неправильное контрольное число';
      }
    }
  }
  return result;
}

/**
 * Валидация СНИЛС (Страховой номер индивидуального лицевого счета)
 * @param snils - СНИЛС для проверки
 * @param error - Объект ошибки
 * @returns Результат валидации
 */
export function validateSnils(snils: string | number, error: ValidationError): boolean {
  let result = false;
  if (typeof snils === 'number') {
    snils = snils.toString();
  } else if (typeof snils !== 'string') {
    snils = '';
  }
  if (!snils.length) {
    error.code = 1;
    error.message = 'СНИЛС пуст';
  } else if (/[^0-9]/.test(snils)) {
    error.code = 2;
    error.message = 'СНИЛС может состоять только из цифр';
  } else if (snils.length !== 11) {
    error.code = 3;
    error.message = 'СНИЛС может состоять только из 11 цифр';
  } else {
    let sum = 0;
    for (let i = 0; i < 9; i++) {
      sum += parseInt(snils[i]) * (9 - i);
    }
    let checkDigit = 0;
    if (sum < 100) {
      checkDigit = sum;
    } else if (sum > 101) {
      checkDigit = parseInt((sum % 101).toString());
      if (checkDigit === 100) {
        checkDigit = 0;
      }
    }
    if (checkDigit === parseInt(snils.slice(-2))) {
      result = true;
    } else {
      error.code = 4;
      error.message = 'Неправильное контрольное число';
    }
  }
  return result;
}

/**
 * Функция для форматирования СНИЛС в формате XXX-XXX-XXX XX
 * @param snils - СНИЛС в виде строки или числа
 * @returns Отформатированный СНИЛС
 */
export function formatSnils(snils: string | number): string {
  if (typeof snils === 'number') {
    snils = snils.toString();
  } else if (typeof snils !== 'string') {
    return '';
  }

  // Убираем все нецифровые символы
  const cleanSnils = snils.replace(/[^\d]/g, '');

  // Проверяем, что у нас есть хотя бы часть цифр
  if (cleanSnils.length === 0) {
    return '';
  }

  // Форматируем СНИЛС в формате XXX-XXX-XXX XX
  let formattedSnils = '';

  if (cleanSnils.length > 0) {
    formattedSnils += cleanSnils.substring(0, Math.min(3, cleanSnils.length));
  }

  if (cleanSnils.length > 3) {
    formattedSnils += '-' + cleanSnils.substring(3, Math.min(6, cleanSnils.length));
  }

  if (cleanSnils.length > 6) {
    formattedSnils += '-' + cleanSnils.substring(6, Math.min(9, cleanSnils.length));
  }

  if (cleanSnils.length > 9) {
    formattedSnils += ' ' + cleanSnils.substring(9, Math.min(11, cleanSnils.length));
  }

  return formattedSnils;
}

/**
 * Проверка корректности СНИЛС без создания объекта ошибки
 * @param snils - СНИЛС для проверки
 * @returns Результат валидации и сообщение об ошибке, если есть
 */
export function checkSnils(snils: string | number): { isValid: boolean; errorMessage?: string } {
  const error: ValidationError = { code: 0, message: '' };
  const isValid = validateSnils(snils, error);

  return {
    isValid,
    errorMessage: isValid ? undefined : error.message,
  };
}
